import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Truck,
  Zap,
  Building,
  Factory,
  HeartPulse,
  ChevronRight,
  ChevronLeft,
  CheckCircle2
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// Animation variants (consistent with other sections)
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const staggerChildren = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

export default function UseCasesSection({ viewport, isRTL }) {
  const { t, i18n } = useTranslation();
  const { isMobile = false } = viewport || {};

  const [activeTab, setActiveTab] = useState('logistics');

  const industriesData = [
    {
      id: 'logistics',
      icon: Truck,
      color: 'hsl(var(--blue-500))',
      imageUrl: 'https://placehold.co/600x400/3B82F6/FFFFFF?text=Logistics'
    },
    {
      id: 'energy',
      icon: Zap,
      color: 'hsl(var(--green-500))',
      imageUrl: 'https://placehold.co/600x400/10B981/FFFFFF?text=Energy'
    },
    {
      id: 'public',
      icon: Building,
      color: 'hsl(var(--sky-500))',
      imageUrl: 'https://placehold.co/600x400/0EA5E9/FFFFFF?text=Public+Sector'
    },
    {
      id: 'manufacturing',
      icon: Factory,
      color: 'hsl(var(--amber-500))',
      imageUrl: 'https://placehold.co/600x400/F59E0B/FFFFFF?text=Manufacturing'
    },
    {
      id: 'healthcare',
      icon: HeartPulse,
      color: 'hsl(var(--red-500))',
      imageUrl: 'https://placehold.co/600x400/EF4444/FFFFFF?text=Healthcare'
    }
  ];

  const industries = industriesData.map(ind => ({
    ...ind,
    name: t(`homepage.useCases.${ind.id}.name`),
    description: t(`homepage.useCases.${ind.id}.description`),
    benefits: [
      t(`homepage.useCases.${ind.id}.benefit1`),
      t(`homepage.useCases.${ind.id}.benefit2`),
      t(`homepage.useCases.${ind.id}.benefit3`),
    ],
    cta: t(`homepage.useCases.${ind.id}.cta`, 'Learn More')
  }));

  const handleNext = () => {
    const currentIndex = industries.findIndex(industry => industry.id === activeTab);
    const nextIndex = (currentIndex + 1) % industries.length;
    setActiveTab(industries[nextIndex].id);
  };

  const handlePrev = () => {
    const currentIndex = industries.findIndex(industry => industry.id === activeTab);
    const prevIndex = currentIndex === 0 ? industries.length - 1 : currentIndex - 1;
    setActiveTab(industries[prevIndex].id);
  };
  
  const currentIndustry = industries.find(ind => ind.id === activeTab);

  return (
    <section
      id="use-cases"
      className="relative py-20 md:py-28 lg:py-32 bg-background overflow-hidden"
    >
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerChildren}
          className="text-center mb-12 md:mb-20 max-w-2xl mx-auto"
        >
          <motion.div variants={fadeInUp}>
            <Badge
              variant="outline"
              className="mb-3 px-3 py-1 text-xs font-medium rounded-full border-primary/30 bg-primary/10 text-primary"
            >
              {t('homepage.useCases.badge', 'Tailored Solutions')}
            </Badge>
          </motion.div>
          <motion.div variants={fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight text-foreground">
              {t('homepage.useCases.title', 'Powering Diverse Industries')}
            </h2>
          </motion.div>
          <motion.div variants={fadeInUp}>
            <p className="text-md md:text-lg text-muted-foreground max-w-xl mx-auto">
              {t('homepage.useCases.subtitle', 'Our platform adapts to unique challenges across sectors, delivering tangible results and driving innovation everywhere.')}
            </p>
          </motion.div>
        </motion.div>

        {isMobile ? (
          <div className="relative">
            <AnimatePresence initial={false} mode='wait'>
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: isRTL ? -300 : 300 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: isRTL ? 300 : -300 }}
                transition={{ type: 'spring', stiffness: 200, damping: 25 }}
                className="w-full"
              >
                {currentIndustry && (
                  <Card className="w-full rounded-xl overflow-hidden shadow-lg bg-card border-border/20">
                    <div className="relative h-48 sm:h-56">
                      <img 
                        src={currentIndustry.imageUrl} 
                        alt={currentIndustry.name} 
                        className="w-full h-full object-cover" 
                        onError={(e) => { e.currentTarget.src = 'https://placehold.co/600x400/secondary/foreground?text=Image+Error'; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className={cn(
                          "absolute top-4 right-4 p-2.5 rounded-lg flex items-center justify-center",
                          "bg-opacity-20 backdrop-blur-sm"
                        )}
                        style={{ backgroundColor: `color-mix(in srgb, ${currentIndustry.color} 30%, transparent)`}}
                      >
                        <currentIndustry.icon size={22} style={{ color: currentIndustry.color }} />
                      </div>
                    </div>
                    <CardContent className="p-5">
                      <h3 className="text-xl font-semibold text-foreground mb-2">{currentIndustry.name}</h3>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{currentIndustry.description}</p>
                      <ul className="space-y-1.5 mb-5">
                        {currentIndustry.benefits.map((benefit, i) => (
                          <li key={i} className="flex items-center text-xs text-muted-foreground">
                            <CheckCircle2 size={14} style={{ color: currentIndustry.color }} className="mr-2 flex-shrink-0" />
                            {benefit}
                          </li>
                        ))}
                      </ul>
                      <Button variant="outline" className="w-full group" style={{borderColor: currentIndustry.color, color: currentIndustry.color}}>
                        {currentIndustry.cta}
                        {isRTL ? <ChevronLeft size={16} className="ml-2 group-hover:-translate-x-1 transition-transform" /> : <ChevronRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />}
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            </AnimatePresence>
            <div className="flex justify-center gap-3 mt-6">
              <Button variant="outline" size="icon" onClick={handlePrev} aria-label={t('common.previous')}>{isRTL ? <ChevronRight size={20}/> : <ChevronLeft size={20}/>}</Button>
              <Button variant="outline" size="icon" onClick={handleNext} aria-label={t('common.next')}>{isRTL ? <ChevronLeft size={20}/> : <ChevronRight size={20}/>}</Button>
            </div>
          </div>
        ) : (
          <Tabs defaultValue="logistics" value={activeTab} onValueChange={setActiveTab} dir={isRTL ? 'rtl' : 'ltr'} className="w-full">
            <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mb-8 h-auto p-1 bg-muted rounded-lg">
              {industries.map((industry) => (
                <TabsTrigger
                  key={industry.id}
                  value={industry.id}
                  className={cn(
                    "flex-col sm:flex-row items-center justify-center gap-2 px-3 py-2.5 h-auto text-xs sm:text-sm font-medium rounded-md transition-all",
                    "data-[state=active]:bg-background data-[state=active]:shadow-md data-[state=active]:text-primary",
                    "hover:bg-background/70"
                  )}
                  style={activeTab === industry.id ? { borderColor: industry.color, color: industry.color } : {}}
                >
                  <industry.icon size={18} className={cn(activeTab === industry.id ? "" : "text-muted-foreground group-hover:text-primary")} />
                  <span>{industry.name}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {industries.map((industry) => (
              <TabsContent key={industry.id} value={industry.id} className="focus-visible:ring-0 focus-visible:ring-offset-0">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    <Card className="overflow-hidden shadow-xl bg-card border-border/20 rounded-xl">
                      <div className="grid md:grid-cols-12 gap-0 items-stretch">
                        <div className="md:col-span-7 lg:col-span-8 p-6 md:p-8 lg:p-10 flex flex-col justify-center">
                          <div className="flex items-center gap-3 mb-3">
                             <div className={cn(
                                "p-2.5 rounded-lg flex items-center justify-center",
                                "bg-opacity-10 "
                              )}
                              style={{ backgroundColor: `color-mix(in srgb, ${industry.color} 15%, transparent)`}}
                            >
                              <industry.icon size={24} style={{color: industry.color}} />
                            </div>
                            <h3 className="text-2xl lg:text-3xl font-bold text-foreground">{industry.name}</h3>
                          </div>
                          <p className="text-muted-foreground mb-6 lg:text-lg leading-relaxed">{industry.description}</p>
                          <ul className="space-y-2.5 mb-8">
                            {industry.benefits.map((benefit, i) => (
                              <li key={i} className="flex items-center text-muted-foreground">
                                <CheckCircle2 size={18} style={{ color: industry.color }} className="mr-2.5 flex-shrink-0" />
                                {benefit}
                              </li>
                            ))}
                          </ul>
                          <Button variant="default" size="lg" className="self-start group bg-primary hover:bg-primary/90">
                            {industry.cta}
                            {isRTL ? <ChevronLeft size={20} className="ml-2 group-hover:-translate-x-1 transition-transform" /> : <ChevronRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />}
                          </Button>
                        </div>
                        <div className="md:col-span-5 lg:col-span-4 relative min-h-[250px] md:min-h-full">
                          <img 
                            src={industry.imageUrl} 
                            alt={industry.name} 
                            className="absolute inset-0 w-full h-full object-cover" 
                            onError={(e) => { e.currentTarget.src = 'https://placehold.co/600x800/secondary/foreground?text=Error'; }}
                          />
                           <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent md:bg-gradient-to-r md:from-black/20 md:via-transparent to-transparent"></div>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                </AnimatePresence>
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>
    </section>
  );
}
